import { Typography, InputLabel, Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { BenefitPlanDto } from 'enrollment/queries/useBenefitPlansQuery/types';
import { PsSelectTuner } from 'stencilSetup';
import { PsSelectTunerBoxStyled } from './DeclineReasonsModalDropdownStyled';
import { BenefitDto } from 'enrollment/queries/useBenefitCategoryQuery/types';

export interface DeclineReasonsModalProps {
  declinePlans?: BenefitPlanDto[];
  orgBenefitName?: string;
  selectedReasonLevelId?: any;
  setSelectedReasonId: (
    value: number | ((prevValue: number) => number)
  ) => void;
  id: string;
  orgBenefitId: string | number;
  passiveBenefits?: BenefitDto[];
}

export const DeclineReasonsModalDropdown = ({
  declinePlans,
  selectedReasonLevelId,
  setSelectedReasonId,
  orgBenefitName,
  id,
  orgBenefitId,
  passiveBenefits,
}: DeclineReasonsModalProps) => {
  const redirectLinkContent = useSelector(
    (state: any) =>
      state?.enrollment?.authentication?.redirectLink === '/enrollment/benefits'
  );
  const { t } = useTranslation();

  const benefitName = orgBenefitName || '';

  const handleSelectionChange = (event: any) => {
    const targetValue = event.target.value;
    if (redirectLinkContent) {
      setSelectedReasonId((prevOptions: any) => {
        const updatedOptions = {
          ...prevOptions,
          [orgBenefitId]: Number(targetValue),
        };

        // Check if all dropdowns have a selected option
        const allSelected =
          Object.keys(updatedOptions).length === passiveBenefits?.length &&
          Object.values(updatedOptions).every(
            (option) => option !== null && option !== undefined
          );

        // If all dropdowns have a selected option, enable the button
        if (allSelected) {
          document
            .querySelector('#decline-passive-benefits-modal_primary')
            ?.removeAttribute('disabled');
        }

        return updatedOptions;
      });
    } else {
      setSelectedReasonId(Number(targetValue));
      document
        .querySelector('#decline-reasons-confirm-btn')
        ?.removeAttribute('disabled');
    }
  };

  useEffect(() => {
    //select-field
    const element = document.getElementById(
      `decline-reason-select-${orgBenefitId}`
    );
    element?.addEventListener('change', (event) => {
      handleSelectionChange(event);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div id={id} data-testid="decline-modal-dropdown">
      <Typography
        variant={redirectLinkContent ? 'body1' : 'h5'}
        className="mb-2"
      >
        {redirectLinkContent
          ? `${t(
              'enrollment.batchDeclineBenefitsModal:declineReasonDropdown.description'
            )}`
          : `Decline ${benefitName} Benefits`}
      </Typography>
      {redirectLinkContent ? (
        <Typography variant="caption" display="block" sx={{ mb: '0.5rem' }}>
          {t(
            'enrollment.batchDeclineBenefitsModal:declineReasonDropdown.dropdownLabel'
          )}
        </Typography>
      ) : (
        <InputLabel id="decline-reason-select-label">
          Reason
          <Box component="span" sx={{ color: 'red', display: 'inline' }}>
            *
          </Box>
        </InputLabel>
      )}
      <PsSelectTunerBoxStyled>
        <PsSelectTuner
          data-testid={`decline-reason-select-${orgBenefitId}`}
          inputId={`decline-reason-select-${orgBenefitId}`}
          label={t(
            'enrollment.batchDeclineBenefitsModal:declineReasonDropdown.dropdownLabel'
          )}
          hideLabel
          value={
            redirectLinkContent
              ? selectedReasonLevelId[orgBenefitId] || 0
              : selectedReasonLevelId
          }
          onPsChange={(e) => handleSelectionChange(e.detail.event as any)}
        >
          <option value="" disabled>
            {' '}
            {redirectLinkContent
              ? `${t('common:select')}...`
              : 'Select a reason'}
          </option>

          {declinePlans?.map((reason) => (
            <option
              value={reason.level_id}
              id={`select-menu-${orgBenefitId}-${reason.level_id}`}
              key={`select-menu-${orgBenefitId}-${reason.level_id}`}
            >
              {reason.level_name}
            </option>
          ))}
        </PsSelectTuner>
      </PsSelectTunerBoxStyled>
    </div>
  );
};
